import * as React from 'react';

import { getBrandingConfig } from '@/branding/config';
import CopyIcon from '@/icons/copy.svg?react';
import TickIcon from '@/icons/tick.svg?react';

const BrandLogo = getBrandingConfig().logos.full;

const APP_URL = 'app.needl.ai';
const COPIED_RESET_MS = 2000;

const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // older webviews have no async clipboard, and it needs a secure context
    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(field);

    return copied;
  }
};

export const AppSunset = () => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = setTimeout(() => setCopied(false), COPIED_RESET_MS);

    return () => clearTimeout(timer);
  }, [copied]);

  const onCopy = async () => {
    if (await copyToClipboard(`https://${APP_URL}`)) {
      setCopied(true);
    }
  };

  return (
    <div className='flex min-h-[100dvh] w-full flex-col items-center justify-center bg-gray-100 px-5 py-10 sm:px-8'>
      <div className='flex w-full max-w-md flex-col items-center gap-6 rounded-lg border border-solid border-gray-300 bg-white px-6 py-10 text-center sm:max-w-lg sm:gap-8 sm:px-12 sm:py-14'>
        <BrandLogo className='h-4 w-auto shrink-0 sm:h-5' />

        <div className='flex flex-col gap-3 sm:gap-4'>
          <h1 className='headline-normal-bold text-gray-900 sm:headline-lg-bold'>
            The mobile app has been discontinued
          </h1>
          <p className='body-sm text-gray-700 sm:body-normal'>
            Needl.ai is now available on the web. Copy the link below, open it
            in your browser, and sign in with the same account.
          </p>
        </div>

        <button
          type='button'
          onClick={onCopy}
          aria-label={`Copy ${APP_URL} to clipboard`}
          className='flex w-full items-center justify-center gap-2 rounded-md bg-emerald-100 px-4 py-3 text-emerald-900 hover:bg-emerald-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-900 focus-visible:ring-offset-2'
        >
          {copied ? (
            <TickIcon className='h-3 w-auto shrink-0 fill-current' />
          ) : (
            <CopyIcon className='h-4 w-4 shrink-0 fill-current' />
          )}
          <span className='body-sm-bold sm:body-normal-bold'>
            {copied ? 'Link copied' : APP_URL}
          </span>
        </button>

        <p className='body-xs text-gray-500'>
          You can remove this app from your device.
        </p>
      </div>
    </div>
  );
};
